import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useCallback, useMemo, useState } from 'react'

/**
 * Custom hook that handles parsing and manipulating keyed text content.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item data, potentially incomplete.
 * @param {boolean} [isContentTeamFromStudioPage] - Whether the current content is from SMM/OM with special permissions.
 * @returns An object containing parsed text and handlers for managing keyed content.
 */

export const useKeyedText = (
    contentItem: Partial<TextContentElement>,
    isContentTeamFromStudioPage?: boolean
) => {
    const text = contentItem?.text || ''
    const hasColonInText = text.includes(':')
    const calculatedIsKeyedText =
        hasColonInText &&
        !isContentTeamFromStudioPage &&
        contentItem.changeType?.toUpperCase() !== 'ADDED' // only show as key if the key is not user added

    // Once content is identified as keyed text, it should remain stable throughout the editing session
    const [isKeyedText] = useState(calculatedIsKeyedText)
    
    // Capture and preserve the initial key for stability during editing session
    const [stableKeyedText] = useState(() => {
        if (!calculatedIsKeyedText) return ': '
        const colonIndex = text.indexOf(':')

        return text.substring(0, colonIndex + 1)
    })

    const { keyedText, keyedTextValue } = useMemo(() => {
        if (!isKeyedText) {
            return { keyedText: ': ', keyedTextValue: undefined }
        }

        // Use the stable key text captured at initialization
        const valuePart = text.includes(':')
            ? text.substring(stableKeyedText.length)
            : undefined

        return {
            keyedText: stableKeyedText,
            keyedTextValue: valuePart,
        }
    }, [text, isKeyedText, stableKeyedText])

    /**
     * Checks if key element already exists in the array to prevent duplication
     */
    const keyElementExists = useCallback(
        (elements: Partial<TextContentElement>[]): boolean => {
            return elements.some(element => 
                element.text?.trim().startsWith(keyedText.trim())
            )
        },
        [keyedText]
    )

    /**
     * Generates unique ID for key element
     */
    const generateKeyElementId = useCallback(
        (elements: Partial<TextContentElement>[]): string => {
            const firstElement = elements[0]
            return firstElement?.id ? `${firstElement.id}_key` : 'generated_key'
        },
        []
    )

    /**
     * Prepends a new element containing only the key text (e.g., "Label: ") to the array,
     * resetting change tracking while preserving the original element's ID.
     */
    const prependKeyedTextElement = useCallback(
        (textElements: Partial<TextContentElement>[]) => {
            if (!isKeyedText || textElements.length === 0) return
            
            // Prevent duplication - exit early if key element already exists
            if (keyElementExists(textElements)) return

            const keyPreservingElement: Partial<TextContentElement> = {
                id: generateKeyElementId(textElements),
                text: keyedText,
                changeType: null,
                authoredUserId: '',
            }

            // prepend the keyedText element at the start of textElements
            textElements.unshift(keyPreservingElement)
        },
        [isKeyedText, keyedText, keyElementExists, generateKeyElementId]
    )

    return {
        keyedText,
        keyedTextValue,
        isKeyedText,
        prependKeyedTextElement,
    }
}
